import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useHistory } from 'react-router-dom';
import useStateWithInputEventHandler from '../../hooks/useStateWithInputEventHandler';
import useSteps from '../../hooks/useSteps';
import { addToSerchParams, isEmailValid } from '../../utils';
import FormWizard from '../FormWizard/FormWizard';
import SwipeTransition from '../SwipeTransition/SwipeTransition';
import InputSection from '../InputSection/InputSection';
import IntroSection from './components/IntroSection/IntroSection';
import useBackendApi from '../../hooks/useBackendApi';
import useClearableState from '../../hooks/useClearableState';
import useLocalSuccessErrMsg from '../../hooks/useLocalSuccessErrMsg';
import FinishStep from './components/FinishStep/FinishStep';

interface ICheckAvailabilityWizardProps {
  open?: boolean;
  providerName?: string;
  className?: string;
  enableTimeStep?: boolean;
  drawerView?: boolean;
  urlToPostDataTo?: string;
  finishTime?: string;
  mobileOrStudio?: string;
  country?: string;
  onClose?: (e?: any) => void;
  onSubmit?: (e: any) => void;
  onFinish?: (e?: any) => void;
}

const setpsHeadingsMapping = {
  start: {
    title: `It's free`,
    subTitle: 'Check Availability',
  },
  time: {
    title: `Finish Time?`,
    subTitle: 'Check Availability',
  },
  name: {
    title: `What's your name?`,
    subTitle: 'Check Availability',
  },
  email: {
    title: `What's your email?`,
    subTitle: 'Check Availability',
  },
  mobile: {
    title: `What's your mobile?`,
    subTitle: 'Check Availability',
  },
  finish: {
    title: `Finish`,
    subTitle: 'Check Availability',
  },
};

const CheckAvailabilityWizard: React.FC<React.PropsWithChildren<React.PropsWithChildren<ICheckAvailabilityWizardProps>>> = ({
  providerName = 'The provider',
  open,
  className,
  enableTimeStep,
  drawerView,
  onClose,
  onFinish,
  urlToPostDataTo = '',
  mobileOrStudio = 'mobile',
  finishTime = '',
  country,
}) => {
  const { step, steps, index, prev, next, isLastStep, isFirstStep, setSteps } =
    useSteps(['start', 'name', 'email', 'mobile', 'finish']);
  const history = useHistory();
  const { post } = useBackendApi();
  const {
    loading,
    setLoading,
    err,
    setErr,
    success,
    setSuccess,
    clearSuccess,
    clearErr,
  } = useLocalSuccessErrMsg();

  const [value, handleInputChange, setValue] = useStateWithInputEventHandler({
    name: '',
    email: '',
    mobile: '',
  });

  React.useEffect(() => {
    if (enableTimeStep) {
      setSteps(['start', 'time', 'name', 'email', 'mobile', 'finish']);
      setValue({ ...value, finishTime });
      return () => {
        setSteps(['start', 'name', 'email', 'mobile', 'finish']);
      };
    }
  }, [enableTimeStep, finishTime]);

  React.useEffect(() => {
    if (open) {
      // set url based on steps
      const search = addToSerchParams(history.location.search, {
        stage: step,
        form: 'checkavailability',
      });
      history.replace({
        pathname: history.location.pathname,
        search,
      });
    }
  }, [step, open]);

  React.useEffect(() => {
    // set state from cache
    const formInput = localStorage.getItem('appointmentFormValues');
    if (formInput && formInput !== 'undefined') {
      const input = JSON.parse(formInput);
      setValue(input as any);
    }
  }, [open]);

  function handleBack() {
    if (isFirstStep) {
      return onClose?.();
    }
    prev();
  }

  const isInputFieldInvalid = React.useMemo(() => {
    switch (step) {
      case 'time':
        return !value.finishTime;
      case 'name':
        return !value.name;
      case 'email':
        return !(value.email && isEmailValid(value.email));
      case 'mobile':
        return !(value.mobile && isValidPhoneNumber(value.mobile));
      default:
        return false;
    }
  }, [value, step]);

  React.useEffect(() => {
    if (isLastStep) {
      success && clearSuccess();
      err && clearErr();
      handleSubmit();
    }
  }, [isLastStep]);

  function handleNext() {
    if (!isInputFieldInvalid) {
      if (isLastStep) {
        return onClose?.();
      }
      next();
    }
  }

  async function handleSubmit() {
    const { name, email, event = '', mobile, finishTime } = value as any;
    const data: any = { name, email, event, mobile };
    if (enableTimeStep || steps.includes('time')) {
      data.finishTime = finishTime;
    }

    localStorage.setItem('appointmentFormValues', JSON.stringify(data));
    if (urlToPostDataTo) {
      setLoading(true);
      const res = await post(
        urlToPostDataTo,
        {
          ...data,
          finishTime,
          mobileOrStudio,
        },
        {
          loader: false,
          onError: (err) => {
            setLoading(false);
            setErr(err.message);
          },
        }
      );
      setLoading(false);
      onFinish?.(res.data);
      setSuccess('We will email and text you once the provider responds');
    }
  }

  function handleClear() {
    handleInputChange({ target: { name: step, value: '' } });
  }

  return (
    <FormWizard
      data-testid="check-availability-wizard"
      className={className}
      open={open}
      onClose={onClose}
      steps={steps}
      activeStep={step}
      onBack={handleBack}
      hideBackBtn={!err && isLastStep}
      title={setpsHeadingsMapping[step]?.title}
      subTitle={setpsHeadingsMapping[step]?.subTitle}
      onNext={handleNext}
      nextBtnLabel={(isLastStep && 'Finish') || 'Next'}
      hideClearBtn={isFirstStep || isLastStep}
      onClear={handleClear}
      drawerView={drawerView}
      loading={loading}
    >
      <SwipeTransition activeStepIndex={index} duration="500">
        <IntroSection providerName={providerName} />
        {enableTimeStep && (
          <InputSection
            name="finishTime"
            type="time"
            autoFocus={!value.finishTime && step === 'time'}
            value={value.finishTime}
            placeholder="Ready by time"
            onChange={handleInputChange}
          />
        )}
        <InputSection
          name="name"
          type="text"
          autoFocus={!value.name && step === 'name'}
          value={value.name}
          onChange={handleInputChange}
        />
        <InputSection
          type="email"
          name="email"
          value={value.email}
          autoFocus={!value.email && step === 'email'}
          onChange={handleInputChange}
          checkListItems={[
            {
              title: `Only used to notify you`,
              subTitle: `When ${providerName} responds we send you an email notification.`,
            },
            {
              title: `Not shared with anyone`,
              subTitle:
                'For your privacy your email is not sent out to the stylist.',
            },
            {
              title: `No marketing emails`,
              subTitle: 'We hate spam. ',
            },
          ]}
        />

        <InputSection
          type="mobile"
          name="mobile"
          country={country}
          value={value.mobile}
          autoFocus={!value.mobile && step === 'mobile'}
          onChange={handleInputChange}
          checkListItems={[
            {
              title: `Only used to notify you`,
              subTitle: `When ${providerName} responds we send you a text notification.`,
            },
            {
              title: `Not shared with anyone`,
              subTitle:
                'For your privacy your mobile is not sent out to the stylist.',
            },
            {
              title: `No calls or marketing texts ever`,
              subTitle: `Cause that's annoying`,
            },
          ]}
        />

        <FinishStep
          loading={loading}
          providerName={providerName}
          success={success}
          err={err}
        />
      </SwipeTransition>
    </FormWizard>
  );
};
export default CheckAvailabilityWizard;
